import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import axios from "axios";
import jsonrepair from "jsonrepair";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import {
  getNameFilterOption,
  getClearProfileModel,
  getUserData,
} from "../../common/EntitiesManagement";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import { generateNoDataValues } from "../../common/utils";
import TableComponent from "../../components/common/TableComponent";
import AddOrEditProfile from "./components/AddOrEditProfile";

export default function Profiles() {
  const {
    profileModel,
    setProfileModel,
    profileModules,
    setProfileModules,
    profileData,
    PROFILE_ENDPOINT,
    config,
    toggleVisibleModalConfirm,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [profileDataFiltered, setProfileDataFiltered] = useState(
    profileData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getNameFilterOption());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const profileDataObj = profileData.Data.find(
        (profileObj) => profileObj.Id === Number(id)
      );
      setProfileModel(profileDataObj);
    }
  }, [id]);

  useEffect(() => {
    setProfileDataFiltered(profileData?.Data);
  }, [profileData]);

  const validateOperationResult = (result, isDelete) => {
    setProfileModel(getClearProfileModel());
    setProfileModules([]);

    if (result.data.ResultCode !== 0) {
      setErrorMessage(JSON.stringify(result.data.Message));
      setShowErrorModal(true);
      return;
    }

    mutate([PROFILE_ENDPOINT, config]);
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await axios.put(
      `${`${PROFILE_ENDPOINT}/inactivate/${itemId}`}`,
      {},
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await axios.put(
      `${`${PROFILE_ENDPOINT}/update`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await axios.post(
      `${PROFILE_ENDPOINT}/create`,
      { ...data, ManagementId: getUserData().ManagementId },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const getRestrictionsCount = (restrictionsJson) => {
    if (restrictionsJson) {
      try {
        const modules = JSON.parse(jsonrepair(restrictionsJson));
        let count = 0;
        modules.forEach((mod) => {
          if (mod.Actions) {
            count += Object.values(mod.Actions).filter(Boolean).length;
          }
        });
        return count;
      } catch {
        const regex = /{Form:/g;
        return (restrictionsJson.match(regex) || []).length;
      }
    }

    return 0;
  };

  const disableSaveChanges = () =>
    !profileModel?.Name ||
    profileModel?.Name.replaceAll(" ", "").length <= 0 ||
    profileModules?.length <= 0;

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_profile}
          onClickToSubmit={async () => {
            await handleUpdateRequest({
              ...profileModel,
              Description: profileModules,
            });
            setShowEditModal(false);
            setProfileModel(getClearProfileModel());
            setProfileModules([]);
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setProfileModel(getClearProfileModel());
            setProfileModules([]);
          }}
          Component={<AddOrEditProfile />}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.profiles}
            subtitle={localize.profile_description}
          />

          <SearcherAndAdd
            entityName="profiles_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_profile}
                validators={disableSaveChanges()}
                onClickToSubmit={async () => {
                  await handleCreateRequest({
                    ...profileModel,
                    Description: profileModules,
                  });
                  setShowAddModal(false);
                  setProfileModel(getClearProfileModel());
                  setProfileModules([]);
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setProfileModel(getClearProfileModel());
                  setProfileModules([]);
                }}
                Component={<AddOrEditProfile />}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={profileModel?.Name}
          entityType={localize.profile}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setProfileModel(getClearProfileModel());
            setProfileModules([]);
            setShowDeleteModal(false);
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(profileModel?.Id);
            setShowDeleteModal(false);
          }}
        />
      )}

      <TableComponent
        firstColumnTitle={localize.actions}
        columnTitles={[localize.name, localize.restrictions]}
        results={
          profileData?.Data?.length > 0
            ? profileDataFiltered?.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setProfileModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setProfileModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  getRestrictionsCount(item.Description),
                ],
              }))
            : [generateNoDataValues(3)]
        }
      />
    </div>
  );
}
